import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { getSessionId, track, EVT } from '@/lib/telemetry';
import { setScreen } from '@/lib/sessionTracking';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 1500;
const TIMEOUT_MS = 30_000;

/**
 * Phase 3.2 — Random matchmaking ("Quick Match") screen.
 *
 * Lifecycle (all polling-based; queue state lives in Mongo):
 *   • mount: POST /api/matchmaking/join. If immediately matched, call
 *     onMatched(session). Otherwise enter polling loop.
 *   • every POLL_MS: GET /api/matchmaking/status?guestId=...
 *       - 'matched'  → onMatched(session)
 *       - 'waiting'  → keep polling; bump elapsed
 *       - 'idle'     → server forgot us (e.g. TTL expired) → show timeout
 *   • after TIMEOUT_MS without match → show 'No opponent found yet' with
 *     Keep Searching / Play vs Bot buttons.
 *   • Cancel button → POST /api/matchmaking/cancel and onCancel().
 *   • unmount → best-effort POST /api/matchmaking/cancel so closing the
 *     screen doesn't leave a ghost in the queue.
 */
export default function MatchmakingScreen({ guest, onMatched, onCancel, onPlayBot }) {
  const [elapsedMs, setElapsedMs] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [error, setError] = useState(null);
  const startRef = useRef(Date.now());
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const cancelledRef = useRef(false);
  const matchedRef = useRef(false);
  const timeoutFiredRef = useRef(false);

  async function joinQueue() {
    try {
      const r = await fetch(`${BACKEND}/api/matchmaking/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guestId: guest.guestId,
          displayName: guest.displayName,
          sessionId: getSessionId(),
        }),
      });
      if (!r.ok) {
        const text = await r.text();
        throw new Error(`Join failed (${r.status}): ${text.slice(0, 200)}`);
      }
      const data = await r.json();
      if (data.status === 'matched') {
        handleMatched(data);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[mm] join error', e);
      setError(String(e.message || e));
    }
  }

  async function poll() {
    if (cancelledRef.current || matchedRef.current) return;
    try {
      const r = await fetch(
        `${BACKEND}/api/matchmaking/status?guestId=${encodeURIComponent(guest.guestId)}`,
      );
      if (!r.ok) {
        const text = await r.text();
        throw new Error(`Status failed (${r.status}): ${text.slice(0, 200)}`);
      }
      const data = await r.json();
      if (cancelledRef.current || matchedRef.current) return;
      if (data.status === 'matched') {
        handleMatched(data);
        return;
      }
      if (data.status === 'idle') {
        // Server forgot us (TTL expired or never enqueued). Re-join silently
        // — but only if we have not since been cancelled.
        if (!cancelledRef.current && !matchedRef.current) {
          await joinQueue();
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[mm] poll error (will retry)', e);
    }
  }

  function handleMatched(data) {
    if (matchedRef.current) return;
    matchedRef.current = true;
    clearInterval(timerRef.current);
    clearInterval(pollRef.current);
    onMatched({
      roomCode: data.roomCode,
      playerId: data.playerId,
      token: data.token,
      name: data.name,
      opponentName: data.opponentName,
    });
  }

  async function handleCancel() {
    if (cancelledRef.current) return;
    cancelledRef.current = true;
    clearInterval(timerRef.current);
    clearInterval(pollRef.current);
    const waitS = Math.max(0, Math.round((Date.now() - startRef.current) / 1000));
    try {
      await fetch(`${BACKEND}/api/matchmaking/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guestId: guest.guestId, sessionId: getSessionId() }),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[mm] cancel error', e);
    }
    try { track(EVT.QUICK_MATCH_CANCELLED, { matchType: 'quick_casual', metadata: { queueCancelAfterSeconds: waitS } }); } catch (_) { /* ignore */ }
    if (onCancel) onCancel();
  }

  function handleKeepSearching() {
    setTimedOut(false);
    startRef.current = Date.now();
    setElapsedMs(0);
    // Re-issue join so we are definitely re-enqueued if server forgot us.
    joinQueue();
  }

  useEffect(() => {
    setScreen('quick_match_queue');
    joinQueue();
    timerRef.current = setInterval(() => {
      if (cancelledRef.current || matchedRef.current) return;
      const dt = Date.now() - startRef.current;
      setElapsedMs(dt);
      if (dt >= TIMEOUT_MS) {
        setTimedOut(true);
        // Only fire expired once, when we first cross the threshold.
        if (!timeoutFiredRef.current) {
          timeoutFiredRef.current = true;
          try { track(EVT.QUICK_MATCH_EXPIRED, { matchType: 'quick_casual', metadata: { queueWaitSeconds: Math.round(dt / 1000) } }); } catch (_) { /* ignore */ }
        }
      }
    }, 250);
    pollRef.current = setInterval(poll, POLL_MS);

    // Fire a preflight-free cancel beacon when the tab/page is being
    // hidden. This complements the React cleanup below and catches the
    // browser-close / hard-navigation case where the React cleanup may
    // race with JS context teardown.
    const beaconCancel = () => {
      if (matchedRef.current || cancelledRef.current) return;
      try {
        const payload = JSON.stringify({ guestId: guest.guestId, sessionId: getSessionId() });
        const blob = new Blob([payload], { type: 'text/plain' });
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
          navigator.sendBeacon(`${BACKEND}/api/matchmaking/cancel`, blob);
        }
      } catch {
        /* noop */
      }
    };
    window.addEventListener('pagehide', beaconCancel);
    window.addEventListener('beforeunload', beaconCancel);

    return () => {
      clearInterval(timerRef.current);
      clearInterval(pollRef.current);
      window.removeEventListener('pagehide', beaconCancel);
      window.removeEventListener('beforeunload', beaconCancel);
      // Best-effort cancel on SPA-unmount. sendBeacon is preflight-free
      // (text/plain blob) and survives navigation; fetch+keepalive on a
      // JSON POST triggers CORS preflight that browsers often drop.
      if (!matchedRef.current && !cancelledRef.current) {
        beaconCancel();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const secs = Math.floor(elapsedMs / 1000);
  const mins = Math.floor(secs / 60);
  const elapsedLabel = mins > 0
    ? `${mins}:${String(secs % 60).padStart(2, '0')}`
    : `0:${String(secs).padStart(2, '0')}`;

  return (
    <div
      data-testid="matchmaking-screen"
      className="kickard-frame stadium-bg flex flex-col items-center justify-center gap-8 py-8 px-6 relative overflow-hidden"
    >
      {/* Stage-light dome */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          background:
            'radial-gradient(ellipse at 50% 16%, hsl(46 95% 60% / 0.18) 0%, transparent 60%)',
        }}
      />

      <div className="relative z-10 flex flex-col items-center gap-3">
        <div
          className="font-heading uppercase text-[hsl(var(--k-gold-bright))]"
          style={{ fontSize: 26, letterSpacing: '0.24em' }}
        >
          QUICK MATCH
        </div>
        <div className="flex items-center gap-3">
          <span className="block w-10 h-px bg-[hsl(var(--sb-rule)/0.6)]" />
          <span
            data-testid="matchmaking-status"
            className="font-body text-[10px] text-white/65 uppercase tracking-[0.36em]"
          >
            {timedOut ? 'No opponent found yet' : 'Searching for opponent'}
          </span>
          <span className="block w-10 h-px bg-[hsl(var(--sb-rule)/0.6)]" />
        </div>
      </div>

      {/* Pulsing player chip */}
      <div className="relative z-10 flex flex-col items-center gap-3">
        <motion.div
          animate={{ scale: !timedOut ? [1, 1.04, 1] : 1, opacity: !timedOut ? [0.85, 1, 0.85] : 1 }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          className="px-6 py-3 rounded-full"
          style={{
            background:
              'linear-gradient(180deg, hsl(0 0% 6% / 0.96) 0%, hsl(0 0% 3% / 0.96) 100%)',
            border: '1px solid hsl(46 65% 35% / 0.65)',
            boxShadow:
              '0 0 0 1px hsl(46 95% 60% / 0.12), 0 16px 40px -16px rgba(0,0,0,0.85)',
          }}
        >
          <div className="font-mono text-[9px] text-white/50 tracking-[0.28em] uppercase text-center">
            Playing as
          </div>
          <div
            data-testid="matchmaking-display-name"
            className="font-heading text-2xl text-white tracking-[0.06em] text-center"
          >
            {guest.displayName}
          </div>
        </motion.div>

        <div
          data-testid="matchmaking-elapsed"
          className="font-mono text-sm text-white/55 tracking-[0.32em] uppercase"
        >
          {elapsedLabel}
        </div>
      </div>

      {error && (
        <div data-testid="matchmaking-error" className="relative z-10 text-red-300 text-center text-sm font-body px-4">
          {error}
        </div>
      )}

      {/* CTA stack */}
      <div className="relative z-10 w-full max-w-sm flex flex-col gap-3">
        {timedOut ? (
          <>
            <button
              data-testid="matchmaking-keep-searching-btn"
              className="btn-primary"
              onClick={handleKeepSearching}
            >
              KEEP SEARCHING
            </button>
            {onPlayBot && (
              <button
                data-testid="matchmaking-play-bot-btn"
                className="btn-secondary"
                onClick={() => {
                  handleCancel();
                  onPlayBot();
                }}
              >
                PLAY VS BOT
              </button>
            )}
            <button
              data-testid="matchmaking-cancel-btn"
              className="btn-secondary"
              onClick={handleCancel}
            >
              CANCEL
            </button>
          </>
        ) : (
          <button
            data-testid="matchmaking-cancel-btn"
            className="btn-secondary"
            onClick={handleCancel}
          >
            CANCEL
          </button>
        )}
      </div>
    </div>
  );
}
